import React, { useEffect, useRef, useState } from "react";
import { Button } from "@texada/ui";
import i18n from "i18n/i18n";
import { getRunningBuildVersion } from "helpers/buildVersion";
import "./DeploymentUpdateBanner.scss";

/** How often to re-check version.json for a newer deployment. */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Drops any Cache Storage entries (service worker / PWA caches) before
 * reloading so the browser cannot serve assets from a previous build. Modern
 * content-hashed assets already guarantee fresh files, but this future-proofs
 * the reload if a service worker is ever added.
 */
async function reloadToLatest(): Promise<void> {
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // Ignore cache eviction errors — the reload below is what matters.
  }
  window.location.reload();
}

async function fetchDeployedVersion(): Promise<string | null> {
  try {
    const res = await fetch(`/version.json?ts=${Date.now()}`, {
      cache: "no-store"
    });
    if (!res.ok) return null;
    const data = await res.json();
    return typeof data?.version === "string" ? data.version : null;
  } catch {
    // Offline / missing file — treat as "no change".
    return null;
  }
}

/**
 * Detects a newer frontend deployment by comparing the build hash served in
 * version.json when the app loaded against the latest version.json, then
 * prompts the user to refresh — no manual cache clearing or .env bump required.
 * Production-only.
 */
const DeploymentUpdateBanner = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const loadedVersion = useRef<string | null>(getRunningBuildVersion());

  useEffect(() => {
    if (process.env.NODE_ENV !== "production") return undefined;

    let active = true;
    const check = async () => {
      const deployed = await fetchDeployedVersion();
      if (!active || !deployed) return;
      if (loadedVersion.current === null) {
        loadedVersion.current = deployed; // baseline captured on first load
      } else if (deployed !== loadedVersion.current) {
        setUpdateAvailable(true);
      }
    };
    const onVisible = () =>
      document.visibilityState === "visible" ? check() : undefined;

    const intervalId = window.setInterval(check, POLL_INTERVAL_MS);
    document.addEventListener("visibilitychange", onVisible);
    check();

    return () => {
      active = false;
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  if (!updateAvailable) return null;

  return (
    <div className="deployment-update-banner alert alert-info" role="alert">
      {i18n.t("a_new_version_is_available")}
      <Button
        className="deployment-update-banner__action"
        variant="primary"
        size="sm"
        onClick={reloadToLatest}
      >
        {i18n.t("refresh_now")}
      </Button>
    </div>
  );
};

export default DeploymentUpdateBanner;
